import React, { useState, useEffect } from 'react';
import { Phone, X } from 'lucide-react';
import { PhoneButton } from './phone-button';
import { PHONE_NUMBERS } from '@/constants/contact';
import { cn } from '@/lib/utils';

interface StickyPhoneProps {
  showOnMobile?: boolean;
  position?: 'bottom-left' | 'bottom-right';
  className?: string;
}

/**
 * Sticky phone button that appears on mobile for immediate contact
 */
export const StickyPhone = ({ 
  showOnMobile = true,
  position = 'bottom-right',
  className 
}: StickyPhoneProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling down 200px
      setIsVisible(window.scrollY > 200);
    };

    if (showOnMobile) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [showOnMobile]);

  if (!showOnMobile || !isVisible) return null;

  const positionClasses = {
    'bottom-left': 'bottom-6 left-6',
    'bottom-right': 'bottom-6 right-6'
  };

  return (
    <div className={cn(
      'fixed z-50 transition-all duration-300 md:hidden',
      positionClasses[position],
      className
    )}>
      {isExpanded ? (
        <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-5 min-w-[300px] animate-scale-in">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-gray-900 text-lg">Need Help?</h3>
              <p className="text-xs text-gray-600 mt-1">Call us now for immediate assistance</p>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
          
          <div className="space-y-3">
            <PhoneButton
              phoneNumber={PHONE_NUMBERS.SALES.href}
              formatted={PHONE_NUMBERS.SALES.formatted}
              label="Sales Inquiries"
              description="Speak with a Security Expert"
              phoneType="sales"
              size="default"
              className="w-full text-sm"
              trackingContext="sticky_mobile"
            />
            
            <PhoneButton
              phoneNumber={PHONE_NUMBERS.SUPPORT.href}
              formatted={PHONE_NUMBERS.SUPPORT.formatted}
              label="Help & Support"
              description="Get Answers Immediately"
              phoneType="support"
              size="default"
              className="w-full text-sm"
              trackingContext="sticky_mobile"
            />
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsExpanded(true)}
          className="bg-gradient-to-r from-edgefi-purple to-edgefi-amethyst text-white p-4 rounded-full shadow-2xl hover:shadow-edgefi-purple/50 transition-all duration-300 hover:scale-110 animate-pulse relative overflow-hidden group"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full"></div>
          <Phone className="h-6 w-6 relative z-10 drop-shadow-sm" />
        </button>
      )}
    </div>
  );
};

export default StickyPhone;